import * as React from "react";
import { useState } from "react";
import { APP_CONFIG } from "../constants/authConfig";
import WelcomePage from "./WelcomePage";
import UserBar from "./UserBar";
import SubscriptionPanel from "./SubscriptionPanel";
import ParserSettings from "./ParserSettings";
import ParseActions from "./ParseActions";
import ParseResults from "./ParseResults";
import Spinner from "./ui/Spinner";
import { useAuth } from "../hooks/useAuth";
import { useXmlToColumns } from "../hooks/useXmlToColumns";

const App = () => {
  const auth = useAuth();
  const {
    mode,
    setMode,
    config,
    updateConfig,
    progress,
    result,
    error,
    isRunning,
    folderFileCount,
    selectFolder,
    clearFolder,
    parseSelection,
    parseFolder,
    pickError,
  } = useXmlToColumns({
    onParseComplete: () => auth.refreshSubscription(auth.email),
  });

  const [settingsOpen, setSettingsOpen] = useState(false);

  const activeCount = [
    config.skipAttributes,
    config.skipDeclaration,
    config.skipComment,
    config.skipNsPrefix,
    config.parseNumbers,
    config.parseBooleans,
    config.trimValues,
  ].filter(Boolean).length;

  if (auth.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Spinner className="h-4 w-4 text-brand-600" />
          Loading session...
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <WelcomePage
        onSignIn={auth.signIn}
        isSubmitting={auth.isSubmitting}
        error={auth.error}
        isChangingEmail={auth.isChangingEmail}
        remainingEmailChanges={auth.remainingEmailChanges}
      />
    );
  }

  const statusLabel = isRunning ? "Running" : auth.hasAccess ? (auth.isTrial ? "Trial" : "Pro") : "Expired";

  return (
    <div className="min-h-screen w-full bg-[radial-gradient(circle_at_top,#dbeafe_0%,#f8fafc_28%,#e2e8f0_100%)]">
      <div className="flex w-full flex-col gap-2">
        <header className="relative overflow-hidden border-b border-white/60 bg-gradient-to-br from-slate-950 via-slate-900 to-brand-700 px-3 py-2.5 text-white shadow-lg">
          <button
            type="button"
            onClick={auth.requestEmailChange}
            disabled={auth.remainingEmailChanges === 0}
            className="absolute right-2.5 top-2.5 shrink-0 rounded-lg border border-white/15 bg-white/10 px-2 py-0.5 text-[10px] font-medium text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Change email
          </button>

          <div className="flex items-center gap-2 pr-20">
            <img
              src="assets/icon-32.png"
              alt={APP_CONFIG.name}
              className="h-7 w-7 shrink-0 rounded-md ring-1 ring-white/15"
            />
            <div className="min-w-0">
              <h1 className="truncate text-xs font-semibold tracking-tight text-white">{APP_CONFIG.name}</h1>
              <UserBar email={auth.email} emailOnly />
              <span className="mt-0.5 inline-flex rounded-full border border-white/10 bg-white/10 px-1.5 py-0.5 text-[9px] font-medium text-slate-100">
                {statusLabel}
              </span>
            </div>
          </div>
        </header>

        {auth.error && (
          <div className="border-y border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{auth.error}</div>
        )}

        <SubscriptionPanel
          hasAccess={auth.hasAccess}
          isTrial={auth.isTrial}
          subscriptionEndDate={auth.subscriptionEndDate}
          onSubscribe={auth.subscribe}
        />

        <section className="card !rounded-none !border-x-0 !p-3">
          <ParseActions
            mode={mode}
            onModeChange={setMode}
            onParse={parseSelection}
            onSelectFolder={selectFolder}
            onParseFolder={parseFolder}
            onClearFolder={clearFolder}
            folderFileCount={folderFileCount}
            isRunning={isRunning}
            progress={progress}
            hasAccess={auth.hasAccess}
            pickError={pickError}
          />
        </section>

        <ParseResults result={result} error={error} />

        <section className="card !rounded-none !border-x-0 !p-0 overflow-hidden">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
            onClick={() => setSettingsOpen((open) => !open)}
            aria-expanded={settingsOpen}
          >
            <div>
              <p className="eyebrow">Configuration</p>
              <h2 className="text-sm font-semibold tracking-tight text-slate-950">Parser settings</h2>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                {activeCount} on
              </span>
              <svg
                className={`h-4 w-4 text-slate-500 transition ${settingsOpen ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {settingsOpen && (
            <div className="border-t border-slate-100 px-3 pb-3 pt-2">
              <ParserSettings config={config} updateConfig={updateConfig} disabled={isRunning} />
            </div>
          )}
        </section>

        <p className="px-3 pb-3 text-center text-[10px] leading-4 text-slate-500">
          Support:{" "}
          <a href={`mailto:${APP_CONFIG.supportEmail}`} className="text-brand-700 underline">
            {APP_CONFIG.supportEmail}
          </a>
        </p>
      </div>
    </div>
  );
};

export default App;
